import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/LanguageSelector';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useLanguage } from '@/i18n/LanguageContext';
import { Menu } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

// Tagline is now in centralized translations as t.header.tagline

export function Header() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const onChange = () => setIsTablet(mql.matches);
    mql.addEventListener('change', onChange);
    setIsTablet(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const closeMenuWithDelay = (callback?: () => void) => {
    // Small delay to show the tap feedback before closing
    setTimeout(() => {
      setIsOpen(false);
      if (callback) {
        setTimeout(callback, 300); // Wait for sheet close animation
      }
    }, 150);
  };

  const handleSectionClick = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    
    const scrollToSection = () => {
      if (location.pathname === '/') {
        const section = document.getElementById(sectionId);
        section?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          const section = document.getElementById(sectionId);
          section?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    };

    closeMenuWithDelay(scrollToSection);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const href = (e.currentTarget as HTMLAnchorElement).getAttribute('href');
    
    closeMenuWithDelay(() => {
      if (href) {
        navigate(href);
      }
    });
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/20 glass overflow-visible pointer-events-none">
      <div className="container flex min-h-20 items-center justify-between py-0 pointer-events-auto">
        <Link
          to="/"
          className="flex items-center gap-2"
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          <BrandLogo size={isMobile ? 'xs' : isTablet ? 'sm' : 'md'} />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 md:flex">
          <Link 
            to="/about" 
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.about}
          </Link>
          <Link 
            to="/faq" 
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.faq}
          </Link>
          <button 
            onClick={handleSectionClick('how-to-use')}
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.howToUse}
          </button>
          <button 
            onClick={handleSectionClick('pricing')}
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.pricing}
          </button>
          <Link 
            to="/gift-store" 
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.giftStore}
          </Link>
          <Link 
            to="/blog" 
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {t.nav.blog}
          </Link>
        </nav>

        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex items-center gap-2">
          <ThemeToggle />
          <LanguageSelector />
          <Link to="/login">
            <Button variant="ghost" size="sm">
              {t.nav.login}
            </Button>
          </Link>
          <Link to="/signup">
            <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
              {t.nav.signup}
            </Button>
          </Link>
        </div>

        {/* Mobile Menu */}
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <LanguageSelector />
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px]">
              <SheetHeader className="text-left">
                <SheetTitle>{({ en: 'Menu', es: 'Menú', pt: 'Menu', fr: 'Menu', ar: 'القائمة', ja: 'メニュー', ko: '메뉴', id: 'Menu' } as Record<string, string>)[language] || 'Menu'}</SheetTitle>
              </SheetHeader>
              
              <nav className="mt-8 flex flex-col gap-2">
                <Link 
                  to="/about" 
                  onClick={handleLinkClick}
                  className="flex items-center py-3 px-4 text-lg font-medium text-foreground rounded-lg hover:bg-muted active:bg-muted/80 transition-all duration-200"
                >
                  {t.nav.about}
                </Link>
                <Link 
                  to="/faq" 
                  onClick={handleLinkClick}
                  className="flex items-center py-3 px-4 text-lg font-medium text-foreground rounded-lg hover:bg-muted active:bg-muted/80 transition-all duration-200"
                >
                  {t.nav.faq}
                </Link>
                <button 
                  onClick={handleSectionClick('how-to-use')}
                  className="flex items-center py-3 px-4 text-lg font-medium text-foreground rounded-lg hover:bg-muted active:bg-muted/80 transition-all duration-200 text-left"
                >
                  {t.nav.howToUse}
                </button>
                <button 
                  onClick={handleSectionClick('pricing')}
                  className="flex items-center py-3 px-4 text-lg font-medium text-foreground rounded-lg hover:bg-muted active:bg-muted/80 transition-all duration-200 text-left"
                >
                  {t.nav.pricing}
                </button>
                <Link 
                  to="/gift-store" 
                  onClick={handleLinkClick}
                  className="flex items-center py-3 px-4 text-lg font-medium text-foreground rounded-lg hover:bg-muted active:bg-muted/80 transition-all duration-200"
                >
                  {t.nav.giftStore}
                </Link>
                <Link 
                  to="/blog" 
                  onClick={handleLinkClick}
                  className="flex items-center py-3 px-4 text-lg font-medium text-foreground rounded-lg hover:bg-muted active:bg-muted/80 transition-all duration-200"
                >
                  {t.nav.blog}
                </Link>
              </nav>

              <div className="mt-8 flex flex-col gap-3 px-4">
                <Link to="/login" onClick={handleLinkClick}>
                  <Button variant="outline" className="w-full transition-all duration-200 active:scale-[0.98]">
                    {t.nav.login}
                  </Button>
                </Link>
                <Link to="/signup" onClick={handleLinkClick}>
                  <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 active:scale-[0.98]">
                    {t.nav.signup}
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
